import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { signUp, confirmSignUp, signIn } from 'core/services/auth.service';
import { useDispatch } from 'react-redux';
import { LoginSignal } from 'presentation/events';
import { mediateLoginRequester } from 'mediators';

// ── Design tokens ────────────────────────────────────────────────────────────

const AC = {
    PURPLE: '#6344d4',
    PURPLE_STRONG: '#6d4ed5',
    PURPLE_DARK: '#5538b8',
    PURPLE_SOFT: '#ede9fe',
    PURPLE_PALE: '#c4b5fd',
    NAVY: '#1e3a8a',
    INK: '#0f172a',
    INK_700: '#334155',
    INK_600: '#475569',
    SLATE: '#64748b',
    SLATE_LIGHT: '#94a3b8',
    HAIR: '#e2e8f0',
    HAIR_SOFT: '#eef2f6',
    BG: '#f8fafc',
    BG_WEB: '#f5f3ff',
    RED: '#dc2626',
    RED_BG: '#fef2f2',
    GREEN: '#16a34a',
    GREEN_BG: '#d1fae5',
    AMBER: '#f59e0b',
    AMBER_BG: '#fef3c7',
};

// ── Icon paths ───────────────────────────────────────────────────────────────

const ICON_PATHS: Record<string, string> = {
    check:        'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z',
    check_circle: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    arrow_right:  'M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z',
    warning:      'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
    refresh:      'M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z',
    sparkle:      'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
    trending_up:  'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    shield:       'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
    bank:         'M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z',
    lock:         'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z',
    chevron_right:'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
};

// ── Inline SVG icon component ────────────────────────────────────────────────

interface IconProps {
    name: string;
    size?: number;
    color?: string;
}

function Icon({ name, size = 18, color = AC.INK }: IconProps) {
    if (name === 'google') {
        return (
            <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: 'inline-block', flexShrink: 0 }}>
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
        );
    }
    if (name === 'apple') {
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill={color || '#000'} style={{ display: 'inline-block', flexShrink: 0 }}>
                <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.45-2.09-.46-3.24 0-1.44.62-2.2.44-3.06-.43C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
        );
    }
    const path = ICON_PATHS[name];
    if (!path) {
        return <span style={{ width: size, height: size, display: 'inline-block' }} />;
    }
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', flexShrink: 0 }}>
            <path d={path} />
        </svg>
    );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const S = {
    root: { width: '100%', height: '100vh', background: '#fff', fontFamily: 'Inter, system-ui, sans-serif', color: AC.INK, display: 'flex', overflow: 'hidden' } as React.CSSProperties,

    // Left panel
    left: { flex: '0 0 480px', maxWidth: 520, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${AC.HAIR}`, padding: '28px 56px 28px', overflow: 'auto' } as React.CSSProperties,
    brand: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em', marginBottom: 48 } as React.CSSProperties,
    brandMark: { width: 32, height: 32, borderRadius: 9, background: `linear-gradient(135deg, ${AC.PURPLE_STRONG} 0%, ${AC.NAVY} 100%)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 } as React.CSSProperties,

    eyebrow: { fontSize: 11, fontWeight: 700, color: AC.PURPLE_STRONG, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 } as React.CSSProperties,
    title: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: '42px' } as React.CSSProperties,
    titleHi: { color: AC.PURPLE_STRONG } as React.CSSProperties,
    sub: { fontSize: 15, color: AC.SLATE, lineHeight: '22px', marginTop: 12, maxWidth: 380 } as React.CSSProperties,

    formStack: { display: 'flex', flexDirection: 'column', gap: 14, marginTop: 32, maxWidth: 380 } as React.CSSProperties,
    field: { display: 'flex', flexDirection: 'column', gap: 6 } as React.CSSProperties,
    fieldLab: { fontSize: 12, fontWeight: 600, color: AC.INK_700 } as React.CSSProperties,

    input: { width: '100%', height: 46, border: `1px solid ${AC.HAIR}`, borderRadius: 10, padding: '0 14px', fontSize: 14, color: AC.INK, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' } as React.CSSProperties,
    inputBig: { fontSize: 18, height: 56, fontWeight: 600 } as React.CSSProperties,
    inputFocused: { borderColor: AC.PURPLE_STRONG, boxShadow: `0 0 0 3px rgba(109,78,213,0.12)` } as React.CSSProperties,
    inputError: { borderColor: AC.RED, boxShadow: `0 0 0 3px rgba(220,38,38,0.10)` } as React.CSSProperties,

    helper: { fontSize: 12, color: AC.SLATE, display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 } as React.CSSProperties,
    helperOk: { color: AC.GREEN } as React.CSSProperties,
    helperWarn: { color: AC.RED } as React.CSSProperties,

    primaryBtn: { height: 50, borderRadius: 10, background: AC.INK, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: 'none', alignSelf: 'flex-start', padding: '0 22px', fontFamily: 'inherit' } as React.CSSProperties,
    primaryBtnDisabled: { background: AC.HAIR, color: AC.SLATE_LIGHT, cursor: 'not-allowed' } as React.CSSProperties,
    primaryBtnFull: { alignSelf: 'stretch' } as React.CSSProperties,

    socialRow: { display: 'flex', gap: 10 } as React.CSSProperties,
    socialBtn: { flex: 1, height: 50, borderRadius: 10, border: `1px solid ${AC.HAIR}`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: AC.SLATE_LIGHT, cursor: 'not-allowed', opacity: 0.6 } as React.CSSProperties,
    divider: { display: 'flex', alignItems: 'center', gap: 10, color: AC.SLATE_LIGHT, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' } as React.CSSProperties,
    dividerLine: { flex: 1, height: 1, background: AC.HAIR } as React.CSSProperties,

    legal: { fontSize: 11, color: AC.SLATE, lineHeight: '16px', marginTop: 14, maxWidth: 380 } as React.CSSProperties,
    legalLink: { color: AC.PURPLE_STRONG, fontWeight: 600 } as React.CSSProperties,

    // OTP
    otpRow: { display: 'flex', gap: 10, marginTop: 8 } as React.CSSProperties,
    otpBox: { width: 54, height: 62, borderRadius: 10, border: `1px solid ${AC.HAIR}`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
    otpBoxFilled: { borderColor: AC.PURPLE_STRONG, background: AC.PURPLE_SOFT, color: AC.PURPLE_STRONG } as React.CSSProperties,
    otpBoxActive: { borderColor: AC.PURPLE_STRONG, boxShadow: `0 0 0 3px rgba(109,78,213,0.12)` } as React.CSSProperties,

    chipGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } as React.CSSProperties,
    chip: { padding: '14px 14px', borderRadius: 10, border: `1px solid ${AC.HAIR}`, background: '#fff', fontSize: 13, fontWeight: 600, color: AC.INK_700, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } as React.CSSProperties,
    chipActive: { borderColor: AC.PURPLE_STRONG, background: AC.PURPLE_SOFT, color: AC.PURPLE_STRONG } as React.CSSProperties,

    progressRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: AC.SLATE, fontWeight: 600, marginBottom: 14 } as React.CSSProperties,
    progressDot: { width: 6, height: 6, borderRadius: '50%', background: AC.HAIR } as React.CSSProperties,
    progressDotActive: { background: AC.PURPLE_STRONG } as React.CSSProperties,
    progressDotDone: { background: AC.GREEN } as React.CSSProperties,

    errBanner: { padding: '10px 12px', background: AC.RED_BG, borderRadius: 10, fontSize: 12, color: AC.RED, display: 'flex', alignItems: 'center', gap: 6 } as React.CSSProperties,

    bottomLinks: { marginTop: 'auto', paddingTop: 32, fontSize: 13, color: AC.SLATE } as React.CSSProperties,
    signinLink: { color: AC.PURPLE_STRONG, fontWeight: 700, marginLeft: 6, cursor: 'pointer' } as React.CSSProperties,

    // Right preview panel
    right: { flex: 1, background: `radial-gradient(ellipse at top right, ${AC.PURPLE_SOFT} 0%, ${AC.BG_WEB} 50%, #fff 100%)`, padding: '56px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', position: 'relative', minWidth: 0 } as React.CSSProperties,
    previewLab: { fontSize: 11, fontWeight: 700, color: AC.SLATE, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 14 } as React.CSSProperties,
    previewCard: { background: '#fff', border: `1px solid ${AC.HAIR}`, borderRadius: 18, boxShadow: '0 24px 60px rgba(15,23,42,0.10)', padding: '24px', display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520, alignSelf: 'center', width: '100%' } as React.CSSProperties,

    previewHead: { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, borderBottom: `1px solid ${AC.HAIR}` } as React.CSSProperties,
    previewAvatar: { width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg, ${AC.PURPLE_STRONG}, ${AC.NAVY})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 } as React.CSSProperties,
    previewHi: { fontSize: 15, fontWeight: 700 } as React.CSSProperties,
    previewSub: { fontSize: 12, color: AC.SLATE, marginTop: 2 } as React.CSSProperties,

    statRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } as React.CSSProperties,
    statBox: { padding: '14px', borderRadius: 12, background: AC.BG, border: `1px solid ${AC.HAIR}` } as React.CSSProperties,
    statLab: { fontSize: 10, fontWeight: 700, color: AC.SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' } as React.CSSProperties,
    statVal: { fontSize: 22, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,

    sparkArea: { height: 80, position: 'relative' } as React.CSSProperties,

    entryRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${AC.HAIR_SOFT}` } as React.CSSProperties,
    entryIco: { width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as React.CSSProperties,

    floatChip: { position: 'absolute', padding: '8px 14px', background: '#fff', borderRadius: 24, boxShadow: '0 8px 24px rgba(15,23,42,0.10)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${AC.HAIR}` } as React.CSSProperties,
};

// ── Progress dots ────────────────────────────────────────────────────────────

const STEP_LABELS = ['Email', 'Verify', 'Profile', 'Done'];

interface ProgressDotsProps {
    stepIndex: number;
}

function ProgressDots({ stepIndex }: ProgressDotsProps) {
    return (
        <div style={S.progressRow} data-testid="progress-dots">
            {STEP_LABELS.map((label, i) => {
                const isActive = i === stepIndex;
                const isDone = i < stepIndex;
                const dotStyle = {
                    ...S.progressDot,
                    ...(isActive ? S.progressDotActive : {}),
                    ...(isDone ? S.progressDotDone : {}),
                };
                const labelColor = isActive ? AC.INK : isDone ? AC.GREEN : AC.SLATE_LIGHT;
                return (
                    <React.Fragment key={label}>
                        <span style={dotStyle} />
                        <span style={{ color: labelColor }}>{label}</span>
                        {i < STEP_LABELS.length - 1 && (
                            <span style={{ color: AC.SLATE_LIGHT, margin: '0 4px' }}>·</span>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ── Right preview panel ──────────────────────────────────────────────────────

interface PreviewCardProps {
    name: string;
}

function PreviewCard({ name }: PreviewCardProps) {
    const displayName = name || 'You';
    const avatarLetter = displayName[0] ? displayName[0].toUpperCase() : 'U';
    const greeting = `Hi, ${displayName}`;

    const entries = [
        { t: 'Rent', a: '-$1,800', iconName: 'lock', bg: AC.PURPLE_SOFT, fg: AC.PURPLE_STRONG },
        { t: 'Salary', a: '+$2,480', iconName: 'trending_up', bg: AC.GREEN_BG, fg: AC.GREEN },
        { t: 'Hydro-Québec', a: '-$156', iconName: 'sparkle', bg: '#FEF3C7', fg: '#D97706' },
    ];

    return (
        <div style={S.right}>
            <div style={{ ...S.floatChip, top: 60, right: 80, color: AC.GREEN }}>
                <Icon name="check_circle" size={14} color={AC.GREEN} />
                On track for May
            </div>
            <div style={{ ...S.floatChip, bottom: 80, left: 80, color: AC.PURPLE_STRONG }}>
                <Icon name="sparkle" size={14} color={AC.PURPLE_STRONG} />
                AI suggestion ready
            </div>

            <div style={S.previewLab}>What you'll see in Uplift</div>
            <div style={S.previewCard}>
                <div style={S.previewHead}>
                    <div style={S.previewAvatar}>{avatarLetter}</div>
                    <div style={{ flex: 1 }}>
                        <div style={S.previewHi}>{greeting}</div>
                        <div style={S.previewSub}>Here's your month at a glance</div>
                    </div>
                    <Icon name="sparkle" size={18} color={AC.PURPLE_STRONG} />
                </div>

                <div style={S.statRow}>
                    <div style={S.statBox}>
                        <div style={S.statLab}>End of month</div>
                        <div style={{ ...S.statVal, color: AC.GREEN }}>+$2,140</div>
                    </div>
                    <div style={S.statBox}>
                        <div style={S.statLab}>Saved this year</div>
                        <div style={S.statVal}>$8,420</div>
                    </div>
                </div>

                <div style={S.sparkArea}>
                    <svg width="100%" height="80" viewBox="0 0 480 80" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="su-g1" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0" stopColor={AC.PURPLE_STRONG} stopOpacity={0.30} />
                                <stop offset="1" stopColor={AC.PURPLE_STRONG} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <path d="M0,60 C40,52 80,30 130,38 C180,46 220,18 280,22 C340,26 380,52 480,12 L480,80 L0,80 Z" fill="url(#su-g1)" />
                        <path d="M0,60 C40,52 80,30 130,38 C180,46 220,18 280,22 C340,26 380,52 480,12" stroke={AC.PURPLE_STRONG} strokeWidth="2.5" fill="none" />
                        {([[40, 52], [130, 38], [280, 22], [380, 52], [460, 16]] as [number, number][]).map(([x, y], i) => (
                            <circle key={i} cx={x} cy={y} r="3" fill="#fff" stroke={AC.PURPLE_STRONG} strokeWidth="2" />
                        ))}
                    </svg>
                </div>

                <div>
                    {entries.map((entry, i) => {
                        const amountColor = entry.a[0] === '+' ? AC.GREEN : AC.INK;
                        return (
                            <div key={i} style={S.entryRow}>
                                <div style={{ ...S.entryIco, background: entry.bg }}>
                                    <Icon name={entry.iconName} size={14} color={entry.fg} />
                                </div>
                                <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{entry.t}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: amountColor }}>{entry.a}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Step 1: Email ────────────────────────────────────────────────────────────

interface StepEmailProps {
    email: string;
    onEmailChange: (v: string) => void;
    onSubmit: () => void;
    loading: boolean;
    error: string;
    emailError: boolean;
}

function StepEmail({ email, onEmailChange, onSubmit, loading, error, emailError }: StepEmailProps) {
    const [focused, setFocused] = useState(false);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const showOkHelper = isValidEmail && !emailError && email.length > 0;

    const inputStyle = {
        ...S.input,
        ...S.inputBig,
        ...(emailError ? S.inputError : focused ? S.inputFocused : {}),
    };

    const btnStyle = loading
        ? { ...S.primaryBtn, ...S.primaryBtnFull, ...S.primaryBtnDisabled }
        : { ...S.primaryBtn, ...S.primaryBtnFull };

    const handleFormSubmit = (evt: React.FormEvent) => {
        evt.preventDefault();
        onSubmit();
    };

    return (
        <form onSubmit={handleFormSubmit}>
            <div style={S.eyebrow}>Step 1 · Get started</div>
            <div style={S.title}>
                Money,<br /><span style={S.titleHi}>finally clear.</span>
            </div>
            <div style={S.sub}>One email is enough — we'll set up the rest in 60 seconds.</div>
            <div style={S.formStack}>
                <div style={S.socialRow}>
                    <button type="button" style={S.socialBtn} disabled title="Coming soon">
                        <Icon name="apple" size={16} color={AC.SLATE_LIGHT} />
                        Apple
                        <span style={{ fontSize: 10, color: AC.SLATE_LIGHT, fontWeight: 600 }}>Soon</span>
                    </button>
                    <button type="button" style={S.socialBtn} disabled title="Coming soon">
                        <Icon name="google" size={16} />
                        Google
                        <span style={{ fontSize: 10, color: AC.SLATE_LIGHT, fontWeight: 600 }}>Soon</span>
                    </button>
                </div>
                <div style={S.divider}>
                    <span style={S.dividerLine} />
                    or with email
                    <span style={S.dividerLine} />
                </div>
                <div style={S.field}>
                    <div style={S.fieldLab}>Work or personal email</div>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        style={inputStyle}
                        required
                        autoFocus
                        placeholder="you@example.com"
                        aria-label="Email address"
                        data-testid="email-input"
                    />
                    {emailError && (
                        <div style={{ ...S.helper, ...S.helperWarn }}>
                            <Icon name="warning" size={13} color={AC.RED} />
                            {error || 'Already in use — sign in instead?'}
                        </div>
                    )}
                    {showOkHelper && !emailError && (
                        <div style={{ ...S.helper, ...S.helperOk }}>
                            <Icon name="check_circle" size={13} color={AC.GREEN} />
                            Looks good
                        </div>
                    )}
                </div>

                {error && !emailError && (
                    <div style={S.errBanner}>
                        <Icon name="warning" size={13} color={AC.RED} />
                        {error}
                    </div>
                )}

                <button type="submit" style={btnStyle} disabled={loading} data-testid="send-code-btn">
                    {loading ? 'Sending…' : 'Send a 6-digit code'}
                    {!loading && <Icon name="arrow_right" size={16} color="#fff" />}
                </button>

                <div style={S.legal}>
                    By continuing you agree to the{' '}
                    <span style={S.legalLink}>Terms</span> &amp;{' '}
                    <span style={S.legalLink}>Privacy</span>.
                </div>
            </div>
        </form>
    );
}

// ── Step 2: Verify OTP ───────────────────────────────────────────────────────

interface StepVerifyProps {
    email: string;
    otp: string[];
    onOtpChange: (otp: string[]) => void;
    onSubmit: () => void;
    onResend: () => void;
    onChangeEmail: () => void;
    loading: boolean;
    error: string;
    resendCountdown: number;
}

function StepVerify({
    email,
    otp,
    onOtpChange,
    onSubmit,
    onResend,
    onChangeEmail,
    loading,
    error,
    resendCountdown,
}: StepVerifyProps) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const allFilled = otp.every((d) => d.length === 1);

    const handleDigitChange = useCallback((index: number, value: string) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...otp];
        next[index] = digit;
        onOtpChange(next);
        if (digit && index < 5) {
            const nextInput = inputRefs.current[index + 1];
            if (nextInput) nextInput.focus();
        }
    }, [otp, onOtpChange]);

    const handleKeyDown = useCallback((index: number, evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = inputRefs.current[index - 1];
            if (prevInput) prevInput.focus();
        }
    }, [otp]);

    const handlePaste = useCallback((evt: React.ClipboardEvent<HTMLInputElement>) => {
        evt.preventDefault();
        const pasted = evt.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const next = Array(6).fill('');
        for (let i = 0; i < pasted.length; i++) {
            next[i] = pasted[i];
        }
        onOtpChange(next);
        const focusIndex = Math.min(pasted.length, 5);
        const targetInput = inputRefs.current[focusIndex];
        if (targetInput) targetInput.focus();
    }, [onOtpChange]);

    const handleFormSubmit = (evt: React.FormEvent) => {
        evt.preventDefault();
        if (allFilled) onSubmit();
    };

    const btnStyle = !allFilled || loading
        ? { ...S.primaryBtn, ...S.primaryBtnFull, ...S.primaryBtnDisabled }
        : { ...S.primaryBtn, ...S.primaryBtnFull };

    const resendLabel = resendCountdown > 0
        ? `Resend (${Math.floor(resendCountdown / 60)}:${String(resendCountdown % 60).padStart(2, '0')})`
        : 'Resend';

    const isExpiredError = error === 'expired' || (error && error.toLowerCase().includes('expired'));
    const isDuplicateError = error && error.toLowerCase().includes('already confirmed');

    return (
        <form onSubmit={handleFormSubmit}>
            <div style={S.eyebrow}>Step 2 · Verify</div>
            <div style={S.title}>Check your <span style={S.titleHi}>inbox</span></div>
            <div style={S.sub}>
                Code sent to <strong style={{ color: AC.INK }}>{email}</strong>. It expires in 10 minutes.
            </div>
            <div style={S.formStack}>
                {(isExpiredError || isDuplicateError) && (
                    <div style={S.errBanner}>
                        <Icon name="warning" size={13} color={AC.RED} />
                        {isExpiredError ? 'Code expired — Resend below.' : error}
                    </div>
                )}
                {error && !isExpiredError && !isDuplicateError && (
                    <div style={S.errBanner}>
                        <Icon name="warning" size={13} color={AC.RED} />
                        {error}
                    </div>
                )}

                <div style={S.otpRow} data-testid="otp-row">
                    {otp.map((digit, i) => {
                        const isFilled = digit.length === 1;
                        const isActive = !isFilled && otp.slice(0, i).every((d) => d.length === 1);
                        const boxStyle = {
                            ...S.otpBox,
                            ...(isFilled ? S.otpBoxFilled : {}),
                            ...(isActive ? S.otpBoxActive : {}),
                            position: 'relative' as const,
                            cursor: 'text',
                        };
                        return (
                            <div key={i} style={boxStyle} data-testid={`otp-box-${i}`}>
                                <input
                                    ref={(el) => { inputRefs.current[i] = el; }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleDigitChange(i, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(i, e)}
                                    onPaste={handlePaste}
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        opacity: 0,
                                        cursor: 'text',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    aria-label={`OTP digit ${i + 1}`}
                                    data-testid={`otp-input-${i}`}
                                />
                                {digit}
                            </div>
                        );
                    })}
                </div>

                <button type="submit" style={btnStyle} disabled={!allFilled || loading} data-testid="verify-btn">
                    {loading ? 'Verifying…' : 'Verify & continue'}
                </button>

                <div style={{ display: 'flex', gap: 18, fontSize: 13, color: AC.SLATE, marginTop: 6, flexWrap: 'wrap' }}>
                    <span>Didn't get it?</span>
                    <button
                        type="button"
                        onClick={onResend}
                        disabled={resendCountdown > 0}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: resendCountdown > 0 ? AC.SLATE_LIGHT : AC.PURPLE_STRONG,
                            fontWeight: 700,
                            cursor: resendCountdown > 0 ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontFamily: 'inherit',
                            fontSize: 13,
                        }}
                        data-testid="resend-btn"
                    >
                        <Icon name="refresh" size={13} color={resendCountdown > 0 ? AC.SLATE_LIGHT : AC.PURPLE_STRONG} />
                        {resendLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onChangeEmail}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: AC.PURPLE_STRONG,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontSize: 13,
                        }}
                    >
                        Use a different email
                    </button>
                </div>
            </div>
        </form>
    );
}

// ── Step 3: Profile ──────────────────────────────────────────────────────────

interface GoalOption {
    id: string;
    label: string;
    iconName: string;
}

const GOAL_OPTIONS: GoalOption[] = [
    { id: 'save', label: 'Save more', iconName: 'trending_up' },
    { id: 'debt', label: 'Pay debt', iconName: 'shield' },
    { id: 'plan', label: 'Plan ahead', iconName: 'sparkle' },
    { id: 'track', label: 'Just track', iconName: 'check_circle' },
];

interface StepProfileProps {
    displayName: string;
    onDisplayNameChange: (v: string) => void;
    goal: string;
    onGoalChange: (id: string) => void;
    onSubmit: () => void;
    loading: boolean;
}

function StepProfile({ displayName, onDisplayNameChange, goal, onGoalChange, onSubmit, loading }: StepProfileProps) {
    const [focused, setFocused] = useState(false);

    const inputStyle = {
        ...S.input,
        ...S.inputBig,
        ...(focused ? S.inputFocused : {}),
    };

    const btnStyle = loading
        ? { ...S.primaryBtn, ...S.primaryBtnFull, ...S.primaryBtnDisabled }
        : { ...S.primaryBtn, ...S.primaryBtnFull };

    const handleFormSubmit = (evt: React.FormEvent) => {
        evt.preventDefault();
        onSubmit();
    };

    return (
        <form onSubmit={handleFormSubmit}>
            <div style={S.eyebrow}>Step 3 · About you</div>
            <div style={S.title}>Last bit of <span style={S.titleHi}>setup</span></div>
            <div style={S.sub}>So your forecasts feel like yours.</div>
            <div style={S.formStack}>
                <div style={S.field}>
                    <div style={S.fieldLab}>Display name</div>
                    <input
                        type="text"
                        value={displayName}
                        onChange={(e) => onDisplayNameChange(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        style={inputStyle}
                        placeholder="Your name"
                        required
                        autoFocus
                        aria-label="Display name"
                        data-testid="display-name-input"
                    />
                </div>
                <div style={S.field}>
                    <div style={S.fieldLab}>Top goal</div>
                    <div style={S.chipGrid}>
                        {GOAL_OPTIONS.map((option) => {
                            const isActive = goal === option.id;
                            const chipStyle = {
                                ...S.chip,
                                ...(isActive ? S.chipActive : {}),
                            };
                            const iconColor = isActive ? AC.PURPLE_STRONG : AC.SLATE;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => onGoalChange(option.id)}
                                    style={{ ...chipStyle, border: 'none', fontFamily: 'inherit' }}
                                    aria-pressed={isActive}
                                    data-testid={`goal-chip-${option.id}`}
                                >
                                    <Icon name={option.iconName} size={14} color={iconColor} />
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <button type="submit" style={btnStyle} disabled={loading} data-testid="finish-setup-btn">
                    {loading ? 'Finishing…' : 'Finish setup'}
                    {!loading && <Icon name="arrow_right" size={16} color="#fff" />}
                </button>
            </div>
        </form>
    );
}

// ── Step 4: Done ─────────────────────────────────────────────────────────────

interface OnboardingOption {
    iconName: string;
    title: string;
    description: string;
    tint: string;
    fg: string;
    action: string;
}

interface StepDoneProps {
    displayName: string;
    onNavigate: (action: string) => void;
}

function StepDone({ displayName, onNavigate }: StepDoneProps) {
    const firstName = displayName.trim().split(' ')[0] || displayName;

    const options: OnboardingOption[] = [
        {
            iconName: 'sparkle',
            title: 'Chat with Uplift',
            description: 'Tell us your situation in plain words',
            tint: AC.PURPLE_SOFT,
            fg: AC.PURPLE_STRONG,
            action: 'suggestions',
        },
        {
            iconName: 'bank',
            title: 'Connect a bank',
            description: 'Read-only · 2 minutes via Plaid',
            tint: '#FEF3C7',
            fg: '#D97706',
            action: 'bank',
        },
        {
            iconName: 'check',
            title: 'Add manually',
            description: "I'll type my first entry",
            tint: AC.HAIR_SOFT,
            fg: AC.SLATE,
            action: 'entries',
        },
    ];

    return (
        <div>
            <div style={S.eyebrow}>You're in 🎉</div>
            <div style={S.title}>
                Welcome,<br /><span style={S.titleHi}>{firstName}</span>.
            </div>
            <div style={S.sub}>Your account is ready. Pick how you'd like to start — you can change this any time.</div>
            <div style={{ ...S.formStack, gap: 10 }}>
                {options.map((opt) => (
                    <button
                        key={opt.action}
                        type="button"
                        onClick={() => onNavigate(opt.action)}
                        style={{
                            display: 'flex',
                            gap: 14,
                            padding: '14px 16px',
                            borderRadius: 12,
                            border: `1px solid ${AC.HAIR}`,
                            background: '#fff',
                            cursor: 'pointer',
                            alignItems: 'center',
                            textAlign: 'left',
                            width: '100%',
                            fontFamily: 'inherit',
                        }}
                        data-testid={`done-option-${opt.action}`}
                    >
                        <div style={{ width: 42, height: 42, borderRadius: 11, background: opt.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon name={opt.iconName} size={18} color={opt.fg} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>{opt.title}</div>
                            <div style={{ fontSize: 12, color: AC.SLATE, marginTop: 2 }}>{opt.description}</div>
                        </div>
                        <Icon name="chevron_right" size={16} color={AC.SLATE_LIGHT} />
                    </button>
                ))}
            </div>
        </div>
    );
}

// ── Main SignUp component ────────────────────────────────────────────────────

type StepName = 'email' | 'verify' | 'profile' | 'done';

const STEP_INDEX: Record<StepName, number> = { email: 0, verify: 1, profile: 2, done: 3 };

// Generate a cryptographically-sufficient random password for the OTP flow.
// Cognito requires a password on signup but the user authenticates via OTP code.
function generatePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let pw = '';
    for (let i = 0; i < 20; i++) {
        pw += chars[Math.floor(Math.random() * chars.length)];
    }
    return pw;
}

export const SignUp = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [loginSignal] = useState(new LoginSignal());

    useEffect(() => {
        return mediateLoginRequester(loginSignal, dispatch).destroy;
    }, [loginSignal, dispatch]);

    const [step, setStep] = useState<StepName>('email');
    const [email, setEmail] = useState('');
    const [password] = useState<string>(generatePassword);
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const [displayName, setDisplayName] = useState('');
    const [goal, setGoal] = useState('save');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(42);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);

    const startCountdown = useCallback((seconds: number) => {
        setResendCountdown(seconds);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
            setResendCountdown((prev) => {
                if (prev <= 1) {
                    if (countdownRef.current) clearInterval(countdownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    useEffect(() => {
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, []);

    // ── Step handlers ──────────────────────────────────────────────────────

    const handleEmailSubmit = async () => {
        setError('');
        setEmailError(false);
        setLoading(true);
        try {
            await signUp(email, password);
            setStep('verify');
            startCountdown(42);
        } catch (err: any) {
            const msg: string = String(err.message || 'Sign up failed');
            if (msg.toLowerCase().includes('already') || msg.toLowerCase().includes('exist')) {
                setEmailError(true);
                setError(msg);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifySubmit = async () => {
        setError('');
        setLoading(true);
        const code = otp.join('');
        try {
            await confirmSignUp(email, code);
            const loginData = await signIn(email, password);
            loginSignal.dispatch(loginData);
            //@ts-ignore
            if (window.ReactNativeWebView) {
                //@ts-ignore
                window.ReactNativeWebView.postMessage(JSON.stringify(loginData));
            }
            setStep('profile');
        } catch (err: any) {
            const msg: string = String(err.message || 'Verification failed');
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCountdown > 0) return;
        setError('');
        setLoading(true);
        try {
            await signUp(email, password);
            startCountdown(42);
        } catch (err: any) {
            // If user already exists, proceed — they can still verify
            startCountdown(42);
        } finally {
            setLoading(false);
        }
    };

    const handleProfileSubmit = () => {
        // Profile data is local-only for now — no backend endpoint.
        // Assumption: displayName is stored only in component state and used for the done screen.
        setStep('done');
    };

    const handleDoneNavigate = (action: string) => {
        if (action === 'suggestions') {
            history.push('/suggestions');
        } else if (action === 'entries') {
            history.push('/entries');
        } else {
            // Bank connection placeholder — navigate home
            history.push('/home');
        }
    };

    // ── Preview name ───────────────────────────────────────────────────────

    const previewName = step === 'profile' || step === 'done'
        ? (displayName || 'You')
        : 'You';

    const stepIndex = STEP_INDEX[step];

    return (
        <div style={S.root}>
            {/* Left form pane */}
            <div style={S.left}>
                {/* Brand */}
                <div style={S.brand}>
                    <div style={S.brandMark}>U</div>
                    Uplift
                </div>

                {/* Progress */}
                <ProgressDots stepIndex={stepIndex} />

                {/* Step content */}
                {step === 'email' && (
                    <StepEmail
                        email={email}
                        onEmailChange={setEmail}
                        onSubmit={handleEmailSubmit}
                        loading={loading}
                        error={error}
                        emailError={emailError}
                    />
                )}
                {step === 'verify' && (
                    <StepVerify
                        email={email}
                        otp={otp}
                        onOtpChange={setOtp}
                        onSubmit={handleVerifySubmit}
                        onResend={handleResend}
                        onChangeEmail={() => { setStep('email'); setOtp(Array(6).fill('')); setError(''); }}
                        loading={loading}
                        error={error}
                        resendCountdown={resendCountdown}
                    />
                )}
                {step === 'profile' && (
                    <StepProfile
                        displayName={displayName}
                        onDisplayNameChange={setDisplayName}
                        goal={goal}
                        onGoalChange={setGoal}
                        onSubmit={handleProfileSubmit}
                        loading={loading}
                    />
                )}
                {step === 'done' && (
                    <StepDone
                        displayName={displayName}
                        onNavigate={handleDoneNavigate}
                    />
                )}

                {/* Sign-in link */}
                <div style={S.bottomLinks}>
                    Have an account?
                    <span
                        style={S.signinLink}
                        onClick={() => history.push('/')}
                        role="link"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') history.push('/'); }}
                    >
                        Sign in
                    </span>
                </div>
            </div>

            {/* Right preview pane */}
            <PreviewCard name={previewName} />
        </div>
    );
};
